import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/react-dom';
import { faListCheck, faSitemap } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { SelectorIcon as ChevronUpDownIcon } from '@heroicons/react/solid';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getTemplateBehavior,
  isAssetDrivenTemplate,
} from '~/components/job/AssetDrivenTemplateIndicator';
import { TEMPLATE_GROUP_ASSET_DRIVEN } from '~/constants/template';
import { JobTemplateFieldsFragment } from '~/gql/graphql';
import { Option } from '~/types';
import { Combolist } from '../Combolist';
import { PopoverStateChangedEffect } from '../PopoverStateChangedEffect';
import { Label } from '../ui/Label';
import { MenuTransition } from '../ui/Transition/MenuTransition';
import { groupTemplateSelectOptions } from '../job/templatePicker';

type Props = {
  label?: string;
  error?: string | false;
  options: Option[];
  value: string;
  onChange: (newValue: string) => void;
  onCreate?: (query: string) => void;
  required?: boolean;
  searchable?: boolean;
  optionsBoxHeight?: string;
  className?: string;
  isAssetDriven?: boolean;
  /** Optional job templates to determine asset-driven vs regular templates for grouping */
  jobTemplates?: readonly JobTemplateFieldsFragment[];
};

export function TemplateSelect({
  label,
  error,
  options,
  value,
  onChange,
  onCreate,
  required,
  searchable = true,
  optionsBoxHeight,
  className,
  isAssetDriven = false,
  jobTemplates,
}: Props) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string>(value);
  const [cancelling, setCancelling] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => setSelected(value), [value]);

  // Create a map of template IDs to their behavior if jobTemplates is provided
  const templateBehaviorMap = useMemo(() => {
    if (!jobTemplates) return new Map<string, 'static' | 'asset_rules'>();
    const map = new Map<string, 'static' | 'asset_rules'>();
    jobTemplates.forEach((template) => {
      map.set(template.id, getTemplateBehavior(template));
    });
    return map;
  }, [jobTemplates]);

  // Group options by asset-driven vs regular templates
  const items = useMemo(() => {
    const behaviorMap =
      jobTemplates && jobTemplates.length > 0 ? templateBehaviorMap : undefined;
    return groupTemplateSelectOptions(options, behaviorMap);
  }, [options, jobTemplates, templateBehaviorMap]);

  // Determine if selected item is asset-driven for icon display
  const selectedItemIsAssetDriven = useMemo(() => {
    if (!jobTemplates || !selected) return isAssetDriven;
    const behavior = templateBehaviorMap.get(selected);
    return behavior ? isAssetDrivenTemplate(behavior) : isAssetDriven;
  }, [jobTemplates, selected, templateBehaviorMap, isAssetDriven]);

  // Group tooltips for Combolist
  const groupTooltips = useMemo(
    () => ({
      [TEMPLATE_GROUP_ASSET_DRIVEN]: t(
        'job:templatePicker.assetDrivenTooltip',
        'These templates create tasks based on selected assets'
      ),
    }),
    [t]
  );

  function handleClosed() {
    if (cancelling) {
      setCancelling(false);
      setSelected(value);
      return;
    }
    onChange(selected);
  }

  const selectedItem = items.find(({ id }) => id === selected);
  const hasSelection = !!selectedItem;

  // Floating UI configuration
  const { refs, floatingStyles } = useFloating({
    open: isOpen,
    placement: 'bottom-start',
    strategy: 'absolute',
    middleware: [
      offset(6),
      size({
        apply({ availableHeight, elements, rects }) {
          // Match reference width and set maxHeight to available space minus fixed button
          const adjustedHeight = Math.max(0, availableHeight - 96); // Account for fixed button
          const minHeight = 250; // Minimum acceptable height

          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            // If we have less than minHeight, set maxHeight to minHeight to trigger flip
            // Otherwise use the available space
            maxHeight:
              availableHeight < minHeight
                ? `${minHeight}px`
                : `${adjustedHeight}px`,
          });
        },
        padding: 6,
      }),
      flip({
        fallbackStrategy: 'initialPlacement',
        fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
        padding: 6,
      }),
      shift({ padding: 6 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  return (
    <Popover
      ref={refs.setReference}
      className={classNames('relative', className)}
    >
      {({ open, close }) => (
        <>
          {label && (
            <Label className='mb-1.5 ml-1 text-sm font-medium text-grey-50'>
              {label}
              {required && <span className='pl-0.5 text-brand'>*</span>}
            </Label>
          )}

          {hasSelection ? (
            <div className='mb-5 flex items-center justify-between rounded-md border border-outline-variant py-2 pl-2 pr-1'>
              <div className='flex flex-1 items-center gap-2.5'>
                <div className='flex items-start gap-2.5 rounded-sm bg-[rgba(0,106,106,0.14)] p-1'>
                  <FontAwesomeIcon
                    icon={selectedItemIsAssetDriven ? faSitemap : faListCheck}
                    className='flex h-5 w-5 items-center justify-center text-brand'
                  />
                </div>
                {selectedItem.name}
              </div>
              <PopoverButton
                as='div'
                className='ml-1 flex cursor-pointer items-center justify-center rounded-full p-2 text-secondary hover:bg-grey-10 hover:text-brand-darker'
                title='Select Template'
              >
                <ChevronUpDownIcon className='h-4 w-4' />
              </PopoverButton>
            </div>
          ) : (
            <>
              <PopoverButton
                as='div'
                className={classNames(
                  'input relative mt-0.5 w-full cursor-pointer overflow-hidden rounded border border-grey-20 bg-white py-3 pl-2 pr-3 leading-5 focus-visible:border-grey-40 focus-visible:outline-none',
                  error && 'border-red-500',
                  label && !error && 'mb-5'
                )}
              >
                <div className='flex items-center'>
                  <span className='text-grey-50'>
                    {selectedItemIsAssetDriven
                      ? t('sitemap', 'sitemap')
                      : 'Select a template'}
                  </span>
                  <ChevronUpDownIcon
                    className='ml-auto h-5 w-5 text-gray-400'
                    aria-hidden='true'
                  />
                </div>
              </PopoverButton>
              {error && <p className='ErrorMessage'>{error}</p>}
            </>
          )}

          <PopoverStateChangedEffect
            open={open}
            onClose={handleClosed}
            onOpenChange={setIsOpen}
          />

          <MenuTransition>
            <PopoverPanel
              ref={refs.setFloating}
              style={floatingStyles}
              className='z-50 flex flex-col overflow-y-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none'
            >
              <Combolist
                items={items}
                searchKeys={['name']}
                placeholder={
                  selectedItemIsAssetDriven
                    ? t('sitemap', 'sitemap')
                    : label || t('Template', 'Template')
                }
                multiple={false}
                value={items.filter(({ id }) => selected === id)}
                onChange={(newSelected) => {
                  // Defensive check: ensure newSelected has at least one item
                  if (newSelected.length > 0 && newSelected[0] != null) {
                    const id = newSelected[0].id;
                    setSelected(id);
                    close();
                  } else {
                    // Treat empty selection as intentional clear
                    setSelected('');
                    close();
                  }
                }}
                renderInner={(item) => item.name}
                searchable={searchable}
                height={optionsBoxHeight || 'h-full'}
                onCreate={onCreate}
                createText='New Template'
                groupTooltips={groupTooltips}
              />
            </PopoverPanel>
          </MenuTransition>
        </>
      )}
    </Popover>
  );
}
